import React, { useEffect, useState, useCallback } from 'react';
import { fetchOzMaliyetOzet } from '../../services/stokV2Service';
import './StokOzMaliyetModal.css';

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  const msg = error?.message || error?.data?.mesaj || error?.data?.message || fallback;
  return msg === 'Failed to fetch' ? 'Sunucuya bağlanılamadı' : msg;
};

const formatCurrency = (amount) => {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  return `${Number(amount).toFixed(2)} ₺`;
};

const StokOzMaliyetModal = ({
  open,
  onClose,
  teknikServisId,
  icerikId,
  parcaAdi,
  parcaKodu,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!open || !teknikServisId || !icerikId) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetchOzMaliyetOzet(teknikServisId, icerikId);
      if (res?.basarili && res?.veri) {
        setData(res.veri);
      } else {
        setData(null);
        setError(res?.mesaj || 'Maliyet bilgisi alınamadı');
      }
    } catch (err) {
      setData(null);
      setError(getErrorMessage(err, 'Maliyet bilgisi yüklenemedi'));
    } finally {
      setLoading(false);
    }
  }, [open, teknikServisId, icerikId]);

  useEffect(() => {
    if (open) load();
    else {
      setData(null);
      setError('');
    }
  }, [open, load]);

  if (!open) return null;

  const ozKatmanlar = data?.ozKatmanlar || [];
  const ozAdet = data?.ozAdet ?? 0;
  const konsinyeAdet = data?.konsinyeAdet ?? 0;
  const toplamOzMaliyet = data?.toplamOzMaliyet ?? ozKatmanlar.reduce(
    (s, k) => s + (Number(k.birimMaliyet) || 0) * (Number(k.toplam ?? k.kalanAdet) || 0),
    0,
  );

  return (
    <div className="modal-overlay stok-oz-maliyet-modal" onClick={onClose} role="presentation">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <i className="fas fa-chart-pie" aria-hidden="true" />
            Öz stok maliyet dağılımı
          </h2>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Kapat">
            <i className="fas fa-times" />
          </button>
        </div>
        <div className="modal-body">
          <p className="stok-oz-maliyet-modal__subtitle">
            <strong>{parcaAdi || data?.parcaAdi || '—'}</strong>
            {parcaKodu || data?.parcaKodu ? ` · ${parcaKodu || data?.parcaKodu}` : ''}
          </p>

          {loading ? (
            <div className="stok-oz-maliyet-modal__loading">
              <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }} />
              Yükleniyor...
            </div>
          ) : error ? (
            <div className="stok-oz-maliyet-modal__empty">{error}</div>
          ) : (
            <>
              <div className="stok-oz-maliyet-modal__summary">
                <div className="stok-oz-maliyet-modal__summary-card is-oz">
                  <div className="label">Öz stok</div>
                  <div className="value">{ozAdet}</div>
                </div>
                <div className="stok-oz-maliyet-modal__summary-card is-konsinye">
                  <div className="label">Konsinye</div>
                  <div className="value">{konsinyeAdet}</div>
                </div>
                <div className="stok-oz-maliyet-modal__summary-card">
                  <div className="label">Toplam öz maliyet</div>
                  <div className="value" style={{ fontSize: '15px' }}>{formatCurrency(toplamOzMaliyet)}</div>
                </div>
              </div>

              <h3 className="stok-oz-maliyet-modal__section-title">Öz stok katmanları</h3>
              {ozKatmanlar.length === 0 ? (
                <div className="stok-oz-maliyet-modal__empty">Öz stok katmanı bulunamadı</div>
              ) : (
                <div className="stok-oz-maliyet-modal__table-wrap">
                  <table className="admins-table stok-oz-maliyet-modal__table">
                    <thead>
                      <tr>
                        <th>Birim fiyat</th>
                        <th style={{ textAlign: 'right' }}>Adet</th>
                        <th style={{ textAlign: 'right' }}>Toplam</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ozKatmanlar.map((k, i) => {
                        const adet = k.toplam ?? k.kalanAdet ?? 0;
                        const birim = Number(k.birimMaliyet) || 0;
                        return (
                          <tr key={k.stokKatmanId || i}>
                            <td>{formatCurrency(birim)}</td>
                            <td style={{ textAlign: 'right' }}>{adet}</td>
                            <td style={{ textAlign: 'right', fontWeight: 600 }}>
                              {formatCurrency(birim * adet)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="stok-oz-maliyet-modal__total-row">
                    <span>Toplam</span>
                    <span>{formatCurrency(toplamOzMaliyet)}</span>
                  </div>
                </div>
              )}

              {konsinyeAdet > 0 && (
                <div className="stok-oz-maliyet-modal__konsinye-note">
                  Konsinye: <strong>{konsinyeAdet} adet</strong> (0 ₺)
                </div>
              )}
            </>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="secondary-btn" onClick={onClose}>
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};

export default StokOzMaliyetModal;
